import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import GeneralTrackingTrackUsageAnalyticsHoverWrapper from 'lumen/components/general/tracking/track-usage-analytics-hover-wrapper';
import ExceptionIndicator from 'lumen/components/hotel/pricing-assistant/exception-indicator';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import ThDayTableSiRow from 'lumen/components/hotel/pricing-assistant/table/cells/day-table/th-day-table-si-row';
import HotelPricingAssistantTableTooltipsTooltipDayTableHeaderCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-day-table-header-cell';
import hasFeature from 'lumen/helpers/has-feature';
import type { Occupancy, Period } from 'lumen/models';

import styles from './th-day-table-value-cell.module.css';

export interface CustomColumn extends CustomEmberTableColumn {
  date: Dayjs;
  getPeriod: (date: Dayjs) => Period | undefined;
  id: string;
  isHeaderLoading: boolean;
  isLastColumn?: boolean;
  onDayHeaderClick?: (period: Period) => void;
}

interface HotelPricingAssistantTableCellsDayTableThDayTableValueCellSignature {
  Args: {
    column: CustomColumn;
  };
}

export default class HotelPricingAssistantTableCellsDayTableThDayTableValueCell extends Component<HotelPricingAssistantTableCellsDayTableThDayTableValueCellSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  get formattedDate(): string {
    return this.args.column.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_SHORT,
    );
  }

  get formattedOccupancyPercentage(): string | undefined {
    return this.occupancyData?.formattedOccupancyPercentage;
  }

  get hasOverride(): boolean {
    return Boolean(this.period?.hasAnyRestrictionOverride);
  }

  get isPastDay(): boolean {
    // Same implementation as in days.isPast function
    return this.hotelAndMarketService.isPast(this.args.column.date);
  }

  get isToday(): boolean {
    return this.args.column.date.isSame(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
  }

  get occupancyData(): Occupancy | undefined {
    return this.period?.day?.occupancy;
  }

  get occupancyPercentage(): number | undefined | null {
    return this.occupancyData?.occupancyPercentage;
  }

  get period(): Period | undefined {
    return this.args.column.getPeriod(this.args.column.date);
  }

  get showDayOverrideModal(): boolean {
    if (this.isPastDay) {
      return false;
    }

    if (
      !(
        this.access.hasPricingAssistantRestrictionsEditRights ||
        this.access.isStaffUser
      )
    ) {
      return false;
    }

    const showForOverbooking =
      this.access.hasPricingAssistantOverbookingRestrictionsFeature;

    const showForMinLos =
      this.access.hasPricingAssistantMinLosRestrictionsFeature;

    return showForOverbooking || showForMinLos;
  }

  get showException(): boolean {
    return Boolean(
      this.period?.shouldShowOverbookingException ||
      this.period?.shouldShowStayRestrictionException,
    );
  }

  get showSmartInsightRow(): boolean {
    return this.access.hasSmartInsights;
  }

  @action
  onHeaderCellClick(): void {
    if (!this.showDayOverrideModal) {
      return;
    }

    if (this.args.column.onDayHeaderClick && this.period) {
      this.args.column.onDayHeaderClick(this.period);
    }
  }

  <template>
    <GeneralTrackingTrackUsageAnalyticsHoverWrapper
      @eventName="Table Header Day Hovered"
      @properties={{hash eventTrigger="table-view"}}
    >
      <div
        class="{{styles.day-cell}}
          grow
          {{if this.isPastDay styles.is-past}}
          relative flex items-stretch"
      >
        <div class="flex flex-1 flex-col items-end gap-100">
          {{! template-lint-disable require-presentational-children }}
          <div
            class="flex w-full flex-1 flex-col items-end gap-100
              {{styles.day-content-hover-zone}}
              {{if this.showSmartInsightRow 'py-100' 'justify-center'}}
              {{if @column.isLastColumn 'pr-400' 'pr-200'}}
              {{if this.showDayOverrideModal 'cursor-pointer'}}"
            data-cell-id={{@column.id}}
            data-test-id="day-cell"
            role="button"
            {{on "click" this.onHeaderCellClick}}
          >
            <div
              class="{{styles.date-section}}
                {{if this.showException styles.has-exception}}"
            >
              <div class="flex items-center gap-100">
                <p
                  class="text-300--medium
                    {{if this.isToday 'px-200'}}
                    {{if this.isToday styles.today-date}}"
                >
                  {{this.formattedDate}}
                </p>
                {{#if this.showException}}
                  <ExceptionIndicator />
                {{else}}
                  <span
                    class={{unless this.hasOverride "invisible"}}
                    data-test-id="override-indicator"
                  >
                    <OverrideIndicator
                      @isDisabled={{this.isPastDay}}
                      @variant="automation-off"
                    />
                  </span>
                {{/if}}
              </div>
              {{#if (hasFeature "pms.otb.view")}}
                {{#if @column.isHeaderLoading}}
                  <div class="flex w-full items-center gap-100">
                    <div class="loading-pulse mt-100 mb-100 flex-1"></div>
                    <span class="invisible"><OverrideIndicator /></span>
                  </div>
                {{else}}
                  <div class="flex items-center gap-100">
                    <p class="text-200--regular {{styles.otb}}">{{t
                        "rates.OTB"
                      }}
                      {{this.formattedOccupancyPercentage}}
                      {{#if this.period.showOccupancyChange}}
                        <GeneralPercentageChangeValue
                          @changeValue={{this.period.occupancyChange}}
                        />
                      {{/if}}
                    </p>
                    <span class="invisible"><OverrideIndicator /></span>
                  </div>
                {{/if}}
              {{/if}}
            </div>
            {{#unless @column.isHeaderLoading}}
              {{#if this.period}}
                <HotelPricingAssistantTableTooltipsTooltipDayTableHeaderCell
                  @date={{@column.date}}
                  @period={{this.period}}
                />
              {{/if}}
            {{/unless}}
          </div>
          {{#if this.showSmartInsightRow}}
            <ThDayTableSiRow
              @date={{@column.date}}
              @isLastColumn={{@column.isLastColumn}}
            />
          {{/if}}
        </div>
      </div>
    </GeneralTrackingTrackUsageAnalyticsHoverWrapper>
  </template>
}
